import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header/Header';
import './ServicesPage.css';

const ServicesPage = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useSelector((state) => state.language);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredServices, setFilteredServices] = useState([]);
  
  // بيانات التأمينات الكاملة حسب المحتوى المقدم
  const insuranceServices = [
    // تأمينات الأشخاص
    {
      id: 1,
      category: 'persons',
      icon: '👤',
      title: {
        en: 'Personal Accident Insurance',
        ar: 'تأمين الحوادث الشخصية'
      },
      description: {
        en: 'Financial protection against accidental injuries, disability, or death. Provides lump sum payments for medical expenses and income replacement.',
        ar: 'حماية مالية ضد الإصابات العرضية، الإعاقة، أو الوفاة. يوفر دفعات مبلغ مقطوع للمصاريف الطبية واستبدال الدخل.'
      },
      features: {
        en: [
          'Accidental death coverage',
          'Permanent total/partial disability',
          'Temporary total disability',
          'Medical expenses reimbursement'
        ],
        ar: [
          'تغطية الوفاة العرضية',
          'إعاقة كلية/جزئية دائمة',
          'إعاقة كلية مؤقتة',
          'استرداد المصاريف الطبية'
        ]
      }
    },
    {
      id: 2,
      category: 'persons',
      icon: '👥',
      title: {
        en: 'Group Life Insurance',
        ar: 'تأمين الحياة الجماعي'
      },
      description: {
        en: 'Life insurance coverage for groups of people, typically employees of a company or members of an organization.',
        ar: 'تغطية تأمين الحياة للمجموعات من الأشخاص، عادة موظفي الشركة أو أعضاء المنظمة.'
      },
      features: {
        en: [
          'Coverage for employee groups',
          'Family protection benefits',
          'Terminal illness coverage',
          'Accidental death benefit'
        ],
        ar: [
          'تغطية لمجموعات الموظفين',
          'مزايا حماية الأسرة',
          'تغطية الأمراض المميتة',
          'مزايا الوفاة العرضية'
        ]
      }
    },
    {
      id: 3,
      category: 'persons',
      icon: '🧳',
      title: {
        en: 'Travelers Insurance',
        ar: 'تأمين المسافرين'
      },
      description: {
        en: 'Comprehensive protection for travelers worldwide. Covers medical emergencies, trip cancellation, lost luggage, and personal accidents.',
        ar: 'حماية شاملة للمسافرين حول العالم. تغطي الطوارئ الطبية، إلغاء الرحلة، فقدان الأمتعة، والحوادث الشخصية.'
      },
      features: {
        en: [
           'Trip cancellation/interruption',
          'Lost/delayed baggage',
          '24/7 emergency assistance'
        ],
        ar: [
           'إلغاء/مقاطعة الرحلة',
          'الأمتعة المفقودة/المتأخرة',
          'مساعدة طوارئ على مدار الساعة'
        ]
      }
    },
    {
      id: 4,
      category: 'persons',
      icon: '⚕️',
      title: {
        en: 'Medical Liability Insurance',
        ar: 'تأمين المسؤولية الطبية'
      },
      description: {
        en: 'Protection for medical professionals against claims of medical malpractice or negligence during practice.',
        ar: 'حماية للأطباء والطواقم الطبية ضد مطالبات الأخطاء الطبية أو الإهمال خلال الممارسة المهنية.'
      },
      features: {
        en: [
          'Malpractice coverage',
          'Legal defense costs',
          'Settlement coverage',
          'License protection'
        ],
        ar: [
          'تغطية الأخطاء الطبية',
          'تكاليف الدفاع القانوني',
          'تغطية التسويات',
          'حماية الرخصة المهنية'
        ]
      }
    },
    {
      id: 5,
      category: 'persons',
      icon: '💰',
      title: {
        en: 'Loan & Credit Insurance',
        ar: 'تأمين الإقراض والائتمان'
      },
      description: {
        en: 'Protection for lenders against borrower default due to death, disability, or unemployment.',
        ar: 'حماية للمقرضين ضد تخلف المقترض عن السداد بسبب الوفاة، الإعاقة، أو البطالة.'
      },
      features: {
        en: [
          'Death benefit coverage',
          'Disability protection',
          'Unemployment coverage',
          'Loan balance payment'
        ],
        ar: [
          'تغطية مزايا الوفاة',
          'حماية الإعاقة',
          'تغطية البطالة',
          'دفع رصيد القرض'
        ]
      }
    },
    
    // التأمين الصحي
    {
      id: 6,
      category: 'health',
      icon: '🏥',
      title: {
        en: 'Medical Insurance',
        ar: 'التأمين الصحي'
      },
      description: {
        en: 'Comprehensive healthcare coverage for individuals, families, and corporate groups. Includes hospitalization, outpatient care, and medication.',
        ar: 'تغطية رعاية صحية شاملة للأفراد والعائلات والمجموعات الشركاتية. تشمل الاستشفاء، الرعاية الخارجية، والأدوية.'
      },
      features: {
        en: [
          'Inpatient hospitalization',
          'Outpatient clinic visits',
          'Emergency room services',
          'Prescription medication'
        ],
        ar: [
          'الاستشفاء الداخلي',
          'زيارات العيادات الخارجية',
          'خدمات غرفة الطوارئ',
          'الأدوية الموصوفة'
        ]
      }
    },
    
    // تأمين المركبات
    {
      id: 7,
      category: 'vehicles',
      icon: '🚗',
      title: {
        en: 'Compulsory Car Insurance',
        ar: 'تأمين المركبات الإجباري'
      },
      description: {
        en: 'Mandatory insurance for all vehicles in Libya as per Libyan law. Covers third-party liability for bodily injury and property damage.',
        ar: 'تأمين إلزامي لجميع المركبات في ليبيا وفقًا للقانون الليبي. يغطي مسؤولية الطرف الثالث للإصابة الجسدية والأضرار المادية.'
      },
      features: {
        en: [
           'Valid throughout Libya',
          'Issued immediately'
        ],
        ar: [
           'ساري المفعول في جميع أنحاء ليبيا',
          'يصدر فورًا'
        ]
      },
      legalReference: {
        en: 'Libyan Insurance Law No. 20 of 2010',
        ar: 'قانون التأمين الليبي رقم 20 لسنة 2010'
      }
    },
    {
      id: 8,
      category: 'vehicles',
      icon: '🚙',
      title: {
        en: 'Comprehensive Car Insurance',
        ar: 'تأمين المركبات الشامل'
      },
      description: {
        en: 'Comprehensive coverage beyond compulsory insurance. Includes theft, fire, natural disasters, and damage to your own vehicle.',
        ar: 'تغطية شاملة تتجاوز التأمين الإلزامي. تشمل السرقة، الحريق، الكوارث الطبيعية، والأضرار لمركبتك الخاصة.'
      },
      features: {
        en: [
          'Coverage for theft and burglary',
          'Fire and natural disaster protection',
          'Accidental damage to your vehicle',
          'Optional glass breakage coverage'
        ],
        ar: [
          'تغطية السرقة والسطو',
          'حماية من الحريق والكوارث الطبيعية',
          'أضرار عرضية لمركبتك',
          'تغطية كسر الزجاج اختيارية'
        ]
      }
    },
    {
      id: 9,
      category: 'vehicles',
      icon: '🚌',
      title: {
        en: 'Third Party Car Insurance',
        ar: 'تأمين المركبات (طرف ثالث)'
      },
      description: {
        en: 'Covers damages caused to third parties including bodily injuries and property damage.',
        ar: 'يغطي الأضرار التي تلحق بالغير بما في ذلك الإصابات الجسدية والأضرار المادية.'
      },
      features: {
        en: [
          'Third party bodily injury',
          'Third party property damage',
          'Legal liability coverage',
          'Court defense costs'
        ],
        ar: [
          'إصابة الطرف الثالث الجسدية',
          'أضرار ممتلكات الطرف الثالث',
          'تغطية المسؤولية القانونية',
          'تكاليف الدفاع في المحكمة'
        ]
      }
    },
    
    // تأمين الممتلكات
    {
      id: 10,
      category: 'property',
      icon: '🔥',
      title: {
        en: 'Fire & Theft Insurance',
        ar: 'تأمين الحريق والسطو'
      },
      description: {
        en: 'Protection against fire and theft damage to residential, commercial, and industrial properties.',
        ar: 'حماية ضد أضرار الحريق والسرقة للممتلكات السكنية والتجارية والصناعية.'
      },
      features: {
        en: [
          'Fire and explosion coverage',
          'Theft and burglary protection',
          'Alternative accommodation costs',
          'Business interruption coverage'
        ],
        ar: [
          'تغطية الحريق والانفجار',
          'حماية السرقة والسطو',
          'تكاليف الإقامة البديلة',
          'تغطية انقطاع الأعمال'
        ]
      }
    },
    
    // التأمين الهندسي
    {
      id: 11,
      category: 'engineering',
      icon: '🏗️',
      title: {
        en: 'Engineering Insurance',
        ar: 'التأمين الهندسي'
      },
      description: {
        en: 'Comprehensive coverage for construction and engineering projects. Protects against material damage, third-party liability, and construction risks.',
        ar: 'تغطية شاملة لمشاريع البناء والهندسة. يحمي من الأضرار المادية، مسؤولية الطرف الثالث، ومخاطر البناء.'
      },
      features: {
        en: [
          'Contractors all-risk insurance',
          'Erection all-risk insurance',
          'Machinery breakdown',
          'Third-party liability'
        ],
        ar: [
          'تأمين جميع مخاطر المقاولين',
          'تأمين جميع مخاطر التركيب',
          'تلف الآلات',
          'مسؤولية الطرف الثالث'
        ]
      }
    },
    
    // تأمين النقل
    {
      id: 12,
      category: 'transport',
      icon: '🚢',
      title: {
        en: 'Marine Insurance',
        ar: 'تأمين النقل البحري'
      },
      description: {
        en: 'Coverage for goods transported by sea against risks during voyage.',
        ar: 'تغطية البضائع المنقولة بحرًا ضد المخاطر خلال الرحلة.'
      },
      features: {
        en: [
          'Cargo damage coverage',
          'Theft and piracy protection',
          'Natural disaster coverage',
          'Transit delay protection'
        ],
        ar: [
          'تغطية أضرار البضائع',
          'حماية السرقة والقرصنة',
          'تغطية الكوارث الطبيعية',
          'حماية تأخير العبور'
        ]
      }
    },
    {
      id: 13,
      category: 'transport',
      icon: '🚚',
      title: {
        en: 'Land Transport Insurance',
        ar: 'تأمين النقل البري'
      },
      description: {
        en: 'Insurance for goods transported by land against risks during transit.',
        ar: 'تأمين البضائع المنقولة برًا ضد المخاطر خلال النقل.'
      },
      features: {
        en: [
          'Road accident coverage',
          'Theft protection',
          'Loading/unloading risks',
          'Transit storage coverage'
        ],
        ar: [
          'تغطية حوادث الطريق',
          'حماية السرقة',
          'مخاطر التحميل/التفريغ',
          'تغطية التخزين أثناء النقل'
        ]
      }
    },
    
    // تأمين الطيران
    {
      id: 14,
      category: 'aviation',
      icon: '🛩️',
      title: {
        en: 'Aviation Insurance',
        ar: 'تأمين الطيران'
      },
      description: {
        en: 'Specialized insurance for aircraft owners, operators, and aviation-related businesses. Covers hull damage, liability, and passenger risks.',
        ar: 'تأمين متخصص لملاك الطائرات والمشغلين والشركات المتعلقة بالطيران. يغطي أضرار الهيكل، المسؤولية، ومخاطر الركاب.'
      },
      features: {
        en: [
          'Hull all-risk coverage',
          'Third-party liability',
          'Passenger legal liability',
          'War and terrorism risk coverage'
        ],
        ar: [
          'تغطية جميع مخاطر الهيكل',
          'مسؤولية الطرف الثالث',
          'المسؤولية القانونية للركاب',
          'تغطية مخاطر الحرب والإرهاب'
        ]
      }
    },
    
    // تأمين الطاقة
    {
      id: 15,
      category: 'energy',
      icon: '⚡',
      title: {
        en: 'Energy Insurance',
        ar: 'تأمين الطاقة'
      },
      description: {
        en: 'Specialized coverage for energy sector projects including oil, gas, and renewable energy. Designed for construction, operation, and liability risks.',
        ar: 'تغطية متخصصة لمشاريع قطاع الطاقة بما في ذلك النفط والغاز والطاقة المتجددة. مصممة لمخاطر البناء والتشغيل والمسؤولية.'
      },
      features: {
        en: [
          'Construction all-risk insurance',
          'Operational risk coverage',
          'Business interruption',
          'Environmental liability'
        ],
        ar: [
          'تأمين جميع مخاطر البناء',
          'تغطية مخاطر التشغيل',
          'انقطاع الأعمال',
          'المسؤولية البيئية'
        ]
      }
    },
    
    // التأمين التكافلي
    {
      id: 16,
      category: 'takaful',
      icon: '🕌',
      title: {
        en: 'Social Takaful Insurance',
        ar: 'التأمين التكافلي الاجتماعي'
      },
      description: {
        en: 'Sharia-compliant cooperative insurance based on mutual assistance and shared responsibility. All operations comply with Islamic principles.',
        ar: 'تأمين تعاوني متوافق مع الشريعة الإسلامية قائم على التعاون والمسؤولية المشتركة. جميع العمليات تتوافق مع المبادئ الإسلامية.'
      },
      features: {
        en: [
          'Sharia-compliant operations',
          'No interest (riba) involved',
          'Risk-sharing principle',
          'Charity (tabarru) contributions'
        ],
        ar: [
          'عمليات متوافقة مع الشريعة',
          'لا تتعامل بالربا',
          'مبدأ تقاسم المخاطر',
          'مساهمات خيرية (تبرع)'
        ]
      }
    },
    
    // تأمين المسؤولية
    {
      id: 17,
      category: 'liability',
      icon: '⚖️',
      title: {
        en: 'Civil Liability Insurance',
        ar: 'تأمين المسؤولية المدنية'
      },
      description: {
        en: 'Protection against third-party liability claims for bodily injury or property damage.',
        ar: 'حماية ضد مطالبات المسؤولية تجاه الغير للإصابة الجسدية أو الأضرار المادية.'
      },
      features: {
        en: [
          'Public liability coverage',
          'Product liability',
          'Professional indemnity',
          'Employers liability'
        ],
        ar: [
          'تغطية المسؤولية العامة',
          'مسؤولية المنتج',
          'التعويض المهني',
          'مسؤولية صاحب العمل'
        ]
      }
    },
    
    // تأمين الأخطار السيبرانية
    {
      id: 18,
      category: 'cyber',
      icon: '🖥️',
      title: {
        en: 'Cyber Risks Insurance',
        ar: 'تأمين الأخطار السيبرانية'
      },
      description: {
        en: 'Protection against cyber attacks, data breaches, and IT system failures.',
        ar: 'حماية ضد الهجمات الإلكترونية، اختراقات البيانات، وفشل أنظمة تكنولوجيا المعلومات.'
      },
      features: {
        en: [
          'Data breach coverage',
          'Ransomware protection',
          'Business interruption',
          'Cyber extortion coverage'
        ],
        ar: [
          'تغطية اختراق البيانات',
          'حماية برامج الفدية',
          'انقطاع الأعمال',
          'تغطية الابتزاز الإلكتروني'
        ]
      }
    },
    
    // تأمين الحروب
    {
      id: 19,
      category: 'war',
      icon: '⚔️',
      title: {
        en: 'War Risks Insurance',
        ar: 'تأمين أخطار الحروب'
      },
      description: {
        en: 'Coverage for damages resulting from wars, terrorism, riots, and civil commotions.',
        ar: 'تغطية الأضرار الناجمة عن الحروب، الإرهاب، الشغب، والاضطرابات المدنية.'
      },
      features: {
        en: [
          'War damage coverage',
          'Terrorism protection',
          'Riot and civil commotion',
          'Sabotage coverage'
        ],
        ar: [
          'تغطية أضرار الحرب',
          'حماية الإرهاب',
          'الشغب والاضطرابات المدنية',
          'تغطية التخريب'
        ]
      }
    },
    
    // تأمين الحماية المدرسية
    {
      id: 20,
      category: 'education',
      icon: '🏫',
      title: {
        en: 'School Protection Insurance',
        ar: 'تأمين الحماية المدرسية'
      },
      description: {
        en: 'Comprehensive protection for educational institutions and students against accidents and liabilities.',
        ar: 'حماية شاملة للمؤسسات التعليمية والطلاب ضد الحوادث والمسؤوليات.'
      },
      features: {
        en: [
          'Student accident coverage',
          'School liability protection',
          'Food poisoning coverage',
          'Educational activities protection'
        ],
        ar: [
          'تغطية حوادث الطلاب',
          'حماية مسؤولية المدرسة',
          'تغطية تسمم الطعام',
          'حماية الأنشطة التعليمية'
        ]
      }
    },
    
    // التأمين المصرفي الشامل
    {
      id: 21,
      category: 'banking',
      icon: '🏦',
      title: {
        en: 'Comprehensive Banking Insurance',
        ar: 'التأمين المصرفي الشامل'
      },
      description: {
        en: 'Complete insurance coverage for banking risks including fraud, theft, and electronic crimes.',
        ar: 'تغطية تأمينية كاملة للمخاطر المصرفية بما في ذلك الاحتيال، السرقة، والجرائم الإلكترونية.'
      },
      features: {
        en: [
          'Fraud and dishonesty coverage',
          'Cash in transit protection',
          'Safe robbery insurance',
          'Electronic crimes coverage'
        ],
        ar: [
          'تغطية الاحتيال وعدم الأمانة',
          'حماية النقدية أثناء النقل',
          'تأمين سرقة الخزائن',
          'تغطية الجرائم الإلكترونية'
        ]
      }
    },
    
    // تأمين الأشياء
    {
      id: 22,
      category: 'objects',
      icon: '📦',
      title: {
        en: 'Objects Insurance',
        ar: 'تأمين الأشياء'
      },
      description: {
        en: 'Coverage for valuable objects, equipment, and assets against theft, damage, loss, or destruction.',
        ar: 'تغطية للأشياء القيمة والمعدات والأصول ضد السرقة، التلف، الفقد، أو التدمير.'
      },
      features: {
        en: [
          'All-risk coverage for valuables',
          'Equipment breakdown',
          'Transit insurance',
          'Stock and inventory coverage'
        ],
        ar: [
          'تغطية جميع المخاطر للأشياء القيمة',
          'تلف المعدات',
          'تأمين النقل',
          'تغطية المخزون والجرد'
        ]
      }
    },
    
    // تأمين المسؤولية عن المنتجات
    {
      id: 23,
      category: 'liability',
      icon: '🏭',
      title: {
        en: 'Product Liability Insurance',
        ar: 'تأمين مسؤولية المنتجات'
      },
      description: {
        en: 'Protection against claims for injuries or damages caused by products manufactured or sold.',
        ar: 'حماية ضد المطالبات للإصابات أو الأضرار الناجمة عن المنتجات المصنعة أو المباعة.'
      },
      features: {
        en: [
          'Manufacturing defects coverage',
          'Design errors protection',
          'Warning defects coverage',
          'Recall expenses coverage'
        ],
        ar: [
          'تغطية عيوب التصنيع',
          'حماية أخطاء التصميم',
          'تغطية عيوب التحذير',
          'تغطية مصاريف سحب المنتج'
        ]
      }
    }
  ];

  // فئات التأمين المحدثة مع تطابق الأسماء
  const categories = [
    { 
      id: 'all', 
      label: { en: 'All Services', ar: 'جميع الخدمات' },
      icon: '🛡️'
    },
    { 
      id: 'persons', 
      label: { en: 'Persons Insurance', ar: 'تأمينات الأشخاص' },
      icon: '👤'
    },
    { 
      id: 'health', 
      label: { en: 'Health Insurance', ar: 'التأمين الصحي' },
      icon: '🏥'
    },
    { 
      id: 'vehicles', 
      label: { en: 'Vehicle Insurance', ar: 'تأمين المركبات' },
      icon: '🚗'
    },
    { 
      id: 'property', 
      label: { en: 'Property Insurance', ar: 'تأمين الممتلكات' },
      icon: '🏠'
    },
    { 
      id: 'engineering', 
      label: { en: 'Engineering Insurance', ar: 'التأمين الهندسي' },
      icon: '🏗️'
    },
    { 
      id: 'transport', 
      label: { en: 'Transport Insurance', ar: 'تأمين النقل' },
      icon: '🚢'
    },
    { 
      id: 'aviation', 
      label: { en: 'Aviation Insurance', ar: 'تأمين الطيران' },
      icon: '✈️'
    },
    { 
      id: 'energy', 
      label: { en: 'Energy Insurance', ar: 'تأمين الطاقة' },
      icon: '⚡'
    },
    { 
      id: 'takaful', 
      label: { en: 'Takaful Insurance', ar: 'التأمين التكافلي' },
      icon: '🕌'
    },
    { 
      id: 'liability', 
      label: { en: 'Liability Insurance', ar: 'تأمين المسؤولية' },
      icon: '⚖️'
    },
    { 
      id: 'cyber', 
      label: { en: 'Cyber Insurance', ar: 'التأمين السيبراني' },
      icon: '💻'
    },
    { 
      id: 'war', 
      label: { en: 'War Risks', ar: 'مخاطر الحروب' },
      icon: '⚠️'
    },
    { 
      id: 'education', 
      label: { en: 'Education', ar: 'التعليم' },
      icon: '🏫'
    },
    { 
      id: 'banking', 
      label: { en: 'Banking', ar: 'المصرفي' },
      icon: '🏦'
    },
    { 
      id: 'objects', 
      label: { en: 'Objects', ar: 'الأشياء' },
      icon: '📦'
    }
  ];

  // فلترة الخدمات
  useEffect(() => {
    let results = insuranceServices;
    
    // فلترة حسب الفئة
    if (activeCategory !== 'all') {
      results = results.filter(service => service.category === activeCategory);
    }
    
    // فلترة حسب البحث
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      results = results.filter(service => 
        service.title[currentLanguage].toLowerCase().includes(term) ||
        service.description[currentLanguage].toLowerCase().includes(term) ||
        service.features[currentLanguage].some(feature => feature.toLowerCase().includes(term))
      );
    }
    
    setFilteredServices(results);
  }, [activeCategory, searchTerm, currentLanguage]);

  // خدماتنا حسب الجمهور المستهدف
  const servicesByAudience = [
    {
      id: 'individuals',
      title: {
        en: 'Services for Individuals',
        ar: 'خدمات الأفراد'
      },
      services: [
        {
          icon: '🏠',
          title: {
            en: 'Property Insurance',
            ar: 'تأمين الممتلكات'
          },
          description: {
            en: 'Protection for your home and personal belongings against fire, theft, and natural disasters.',
            ar: 'حماية لمنزلك وممتلكاتك الشخصية ضد الحريق، السرقة، والكوارث الطبيعية.'
          }
        },
        {
          icon: '🚗',
          title: {
            en: 'Vehicle Insurance',
            ar: 'تأمين المركبات'
          },
          description: {
            en: 'Comprehensive coverage for your cars with various options to suit your needs.',
            ar: 'تغطية شاملة لسياراتك مع خيارات متنوعة تناسب احتياجاتك.'
          }
        },
        {
          icon: '✈️',
          title: {
            en: 'Travel Insurance',
            ar: 'تأمين السفر'
          },
          description: {
            en: 'Peace of mind during your travels with medical and luggage protection.',
            ar: 'راحة البال خلال سفرك مع حماية طبية وحماية للأمتعة.'
          }
        }
      ]
    },
    {
      id: 'smb',
      title: {
        en: 'Services for Small & Medium Businesses',
        ar: 'خدمات الشركات الصغرى والمتوسطة'
      },
      services: [
        {
          icon: '🏥',
          title: {
            en: 'Health Insurance',
            ar: 'التأمين الصحي'
          },
          description: {
            en: 'Group health insurance for your employees with VIP services.',
            ar: 'تأمين صحي جماعي لموظفيك مع خدمات VIP.'
          }
        },
        {
          icon: '🛡️',
          title: {
            en: 'Social Takaful',
            ar: 'التكافل الاجتماعي'
          },
          description: {
            en: 'Sharia-compliant insurance for financial protection in cases of death or disability.',
            ar: 'تأمين متوافق مع الشريعة للحماية المالية في حالات الوفاة أو العجز.'
          }
        },
        {
          icon: '🔥',
          title: {
            en: 'Fire & Theft Insurance',
            ar: 'تأمين الحريق والسطو'
          },
          description: {
            en: 'Protection for business properties and production facilities.',
            ar: 'حماية الممتلكات التجارية والمرافق الإنتاجية.'
          }
        }
      ]
    },
    {
      id: 'government',
      title: {
        en: 'Services for Institutions & Government Sector',
        ar: 'خدمات المؤسسات والقطاع الحكومي'
      },
      services: [
        {
          icon: '✈️',
          title: {
            en: 'Aviation Insurance',
            ar: 'تأمين الطيران'
          },
          description: {
            en: 'Specialized coverage for airlines and aviation operations.',
            ar: 'تغطية متخصصة لشركات الطيران وعمليات الطيران.'
          }
        },
        {
          icon: '💻',
          title: {
            en: 'Cyber Risks Insurance',
            ar: 'تأمين الأخطار السيبرانية'
          },
          description: {
            en: 'Protection against cyber attacks and data breaches.',
            ar: 'حماية ضد الهجمات الإلكترونية واختراقات البيانات.'
          }
        },
        {
          icon: '🏗️',
          title: {
            en: 'Engineering Insurance',
            ar: 'التأمين الهندسي'
          },
          description: {
            en: 'Coverage for engineering projects and construction risks.',
            ar: 'تغطية للمشاريع الهندسية ومخاطر البناء.'
          }
        }
      ]
    },
    {
      id: 'banking',
      title: {
        en: 'Services for Banking Sector',
        ar: 'خدمات القطاع المصرفي'
      },
      services: [
        {
          icon: '🏦',
          title: {
            en: 'Comprehensive Banking Insurance',
            ar: 'التأمين المصرفي الشامل'
          },
          description: {
            en: 'Complete protection for banking operations and financial crimes.',
            ar: 'حماية كاملة للعمليات المصرفية والجرائم المالية.'
          }
        },
        {
          icon: '💰',
          title: {
            en: 'Credit Insurance',
            ar: 'تأمين الائتمان'
          },
          description: {
            en: 'Protection for banks against loan defaults.',
            ar: 'حماية المصارف ضد تخلف القروض عن السداد.'
          }
        }
      ]
    }
  ];

  // الأسئلة الشائعة
  const faqItems = [
    {
      question: {
        en: 'How to choose the right insurance?',
        ar: 'كيف أختار التأمين المناسب؟'
      },
      answer: {
        en: 'Consider your assets, risks, and budget. Our experts can help you assess your needs and recommend suitable coverage. We offer free consultations to help you make the best decision.',
        ar: 'فكر في أصولك ومخاطرك وميزانيتك. يمكن لخبرائنا مساعدتك في تقييم احتياجاتك والتوصية بالتغطية المناسبة. نقدم استشارات مجانية لمساعدتك في اتخاذ أفضل قرار.'
      }
    },
    {
      question: {
        en: 'What documents are required?',
        ar: 'ما هي المستندات المطلوبة؟'
      },
      answer: {
        en: 'Typically: ID copy, proof of ownership, and previous insurance details (if applicable). Requirements vary by insurance type. Our team will guide you through the specific requirements for your chosen insurance.',
        ar: 'عادة: نسخة من الهوية، إثبات الملكية، وتفاصيل التأمين السابق (إن وجد). المتطلبات تختلف حسب نوع التأمين. سيرشدك فريقنا خلال المتطلبات المحددة للتأمين الذي تختاره.'
      }
    },
    {
      question: {
        en: 'How long does processing take?',
        ar: 'كم تستغرق المعالجة؟'
      },
      answer: {
        en: 'Compulsory car insurance: immediate. Other insurances: 1-3 business days depending on complexity and documents. We strive to make the process as quick and efficient as possible.',
        ar: 'تأمين المركبات الإلزامي: فوري. أنواع التأمين الأخرى: 1-3 أيام عمل حسب التعقيد والمستندات. نسعى جاهدين لجعل العملية سريعة وفعالة قدر الإمكان.'
      }
    },
    {
      question: {
        en: 'Can I cancel my policy?',
        ar: 'هل يمكنني إلغاء وثيقتي؟'
      },
      answer: {
        en: 'Yes, most policies can be cancelled. Refunds are calculated based on elapsed time and policy terms. We have a transparent cancellation policy to ensure fairness for all our clients.',
        ar: 'نعم، يمكن إلغاء معظم الوثائق. يتم حساب المبالغ المستردة بناءً على الوقت المنقضى وشروط الوثيقة. لدينا سياسة إلغاء شفافة لضمان العدالة لجميع عملائنا.'
      }
    },
    {
      question: {
        en: 'Are your services Sharia compliant?',
        ar: 'هل خدماتكم متوافقة مع الشريعة؟'
      },
      answer: {
        en: 'Yes, we offer Takaful (Islamic insurance) services that are fully Sharia compliant. Our Takaful products operate on the principles of mutual cooperation and shared responsibility.',
        ar: 'نعم، نقدم خدمات التكافل (التأمين الإسلامي) المتوافقة تمامًا مع الشريعة. تعمل منتجات التكافل لدينا على مبادئ التعاون المتبادل والمسؤولية المشتركة.'
      }
    },
    {
      question: {
        en: 'Do you cover war and terrorism risks?',
        ar: 'هل تغطون مخاطر الحرب والإرهاب؟'
      },
      answer: {
        en: 'Yes, we offer specialized war risks insurance covering damages from wars, terrorism, riots, and civil commotions, subject to policy terms and conditions.',
        ar: 'نعم، نقدم تأمين أخطار الحروب المتخصص الذي يغطي الأضرار الناجمة عن الحروب، الإرهاب، الشغب، والاضطرابات المدنية، وفقًا لشروط وأحكام الوثيقة.'
      }
    }
  ];

  // معالجة تغيير الفئة
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    
    // التمرير إلى قسم الخدمات
    setTimeout(() => {
      const servicesSection = document.querySelector('.services-list');
      if (servicesSection) {
        servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // معالجة البحث
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  return (
    <div className="services-page">
      <Header />
      
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <h1 className="hero-title">
              {currentLanguage === 'en' 
                ? 'Our Insurance Services' 
                : 'خدماتنا التأمينية'}
            </h1>
            <p className="hero-subtitle">
              {currentLanguage === 'en' 
                ? 'Comprehensive protection solutions tailored to your needs. Discover our range of insurance services designed to provide peace of mind and financial security.' 
                : 'حلول حماية شاملة مصممة خصيصًا لاحتياجاتك. اكتشف مجموعة خدماتنا التأمينية المصممة لتوفير راحة البال والأمن المالي.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* الفلاتر والبحث */}
      <section className="services-filters">
        <div className="container">
          <div className="filters-container">
            {/* شريط البحث */}
            <div className="search-container">
              <input
                type="text"
                placeholder={currentLanguage === 'en' ? 'Search services...' : 'ابحث في الخدمات...'}
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>

            {/* فئات التأمين */}
            <div className="categories-container">
              <div className="categories-scroll">
                {categories.map((category) => (
                  <motion.button
                    key={category.id}
                    className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                    onClick={() => handleCategoryChange(category.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    data-category={category.id}
                  >
                    <span className="category-icon">{category.icon}</span>
                    <span className="category-label">{category.label[currentLanguage]}</span>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services by Audience */}
      <section className="audience-services">
        <div className="container">
          <h2 className="section-title">
            {currentLanguage === 'en' ? 'Services by Audience' : 'الخدمات حسب الجمهور المستهدف'}
          </h2>
          
          <div className="audience-grid">
            {servicesByAudience.map((audience, index) => (
              <motion.div
                key={audience.id}
                className="audience-card"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <h3 className="audience-title">{audience.title[currentLanguage]}</h3>
                
                <div className="audience-services-list">
                  {audience.services.map((service, serviceIndex) => (
                    <div key={serviceIndex} className="audience-service-item">
                      <span className="service-item-icon">{service.icon}</span>
                      <div className="service-item-content">
                        <h4>{service.title[currentLanguage]}</h4>
                        <p>{service.description[currentLanguage]}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <motion.button
                  className="audience-cta"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/contact')}
                >
                  {currentLanguage === 'en' ? 'Get Quote' : 'احصل على عرض سعر'}
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* قائمة الخدمات */}
      <section className="services-list">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              {currentLanguage === 'en' 
                ? `All Insurance Services` 
                : `جميع خدمات التأمين`}
            </h2>
            
            {activeCategory !== 'all' && (
              <div className="active-filter">
                <span className="filter-badge">
                  {categories.find(c => c.id === activeCategory)?.label[currentLanguage]}
                </span>
                <button 
                  className="clear-filter"
                  onClick={() => setActiveCategory('all')}
                >
                  ✕ {currentLanguage === 'en' ? 'Clear Filter' : 'مسح الفلتر'}
                </button>
              </div>
            )}
            
            {searchTerm && (
              <div className="search-results-info">
                <span className="results-count">
                  {filteredServices.length} {currentLanguage === 'en' ? 'services found' : 'خدمة وجدت'}
                </span>
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  ✕ {currentLanguage === 'en' ? 'Clear Search' : 'مسح البحث'}
                </button>
              </div>
            )}
          </div>
          
          {filteredServices.length === 0 ? (
            <div className="no-results">
              <span className="no-results-icon">🔍</span>
              <h3>{currentLanguage === 'en' ? 'No services found' : 'لم يتم العثور على خدمات'}</h3>
              <p>
                {currentLanguage === 'en' 
                  ? 'Try changing your search criteria or select a different category.' 
                  : 'حاول تغيير معايير البحث أو اختر فئة مختلفة.'}
              </p>
              <div className="no-results-actions">
                <button 
                  className="reset-filters"
                  onClick={() => {
                    setActiveCategory('all');
                    setSearchTerm('');
                  }}
                >
                  {currentLanguage === 'en' ? 'Reset All Filters' : 'إعادة تعيين جميع الفلاتر'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="results-count-badge">
                {filteredServices.length} {currentLanguage === 'en' ? 'Services Available' : 'خدمة متاحة'}
              </div>
              
              <div className="services-grid">
                {filteredServices.map((service, index) => (
                  <motion.div
                    key={service.id}
                    className="service-card"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)' }}
                  >
                    <div className="service-icon">
                      <span>{service.icon}</span>
                    </div>
                    
                    <div className="service-content">
                      <div className="service-category">
                        <span className="category-tag">
                          {categories.find(c => c.id === service.category)?.icon} 
                          {categories.find(c => c.id === service.category)?.label[currentLanguage]}
                        </span>
                      </div>
                      
                      <h3>{service.title[currentLanguage]}</h3>
                      <p className="service-description">{service.description[currentLanguage]}</p>
                      
                      <div className="service-features">
                        <h4>{currentLanguage === 'en' ? 'Key Features:' : 'المميزات الرئيسية:'}</h4>
                        <ul>
                          {service.features[currentLanguage].map((feature, idx) => (
                            <li key={idx}>{feature}</li>
                          ))}
                        </ul>
                      </div>

                      {service.legalReference && (
                        <div className="legal-reference">
                          <strong>{currentLanguage === 'en' ? 'Legal Reference:' : 'المرجع القانوني:'}</strong>
                          <p>{service.legalReference[currentLanguage]}</p>
                        </div>
                      )}
                    </div>

                    <div className="service-actions">
                      <motion.button
                        className="inquire-btn"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          localStorage.setItem('selectedService', JSON.stringify({
                            title: service.title[currentLanguage],
                            category: service.category
                          }));
                          navigate('/contact');
                        }}
                      >
                        {currentLanguage === 'en' ? 'Request Quote' : 'طلب عرض سعر'}
                      </motion.button>
                      <motion.button
                        className="details-btn"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          const details = {
                            title: service.title[currentLanguage],
                            description: service.description[currentLanguage],
                            features: service.features[currentLanguage],
                            icon: service.icon
                          };
                          localStorage.setItem('serviceDetails', JSON.stringify(details));
                          navigate('/contact');
                        }}
                      >
                        {currentLanguage === 'en' ? 'More Details' : 'المزيد من التفاصيل'}
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* قسم الأسئلة الشائعة */}
      <section className="faq-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">
              {currentLanguage === 'en' ? 'Frequently Asked Questions' : 'الأسئلة الشائعة'}
            </h2>
            
            <div className="faq-grid">
              {faqItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="faq-item"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h3 className="faq-question">
                    <span className="faq-icon">❓</span>
                    {item.question[currentLanguage]}
                  </h3>
                  <p className="faq-answer">{item.answer[currentLanguage]}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* قسم الـ CTA */}
      <section className="services-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2 className="cta-title">
              {currentLanguage === 'en' ? 'Need Personalized Advice?' : 'تحتاج إلى نصيحة شخصية؟'}
            </h2>
            <p className="cta-text">
              {currentLanguage === 'en' 
                ? 'Our insurance experts are ready to help you choose the perfect coverage for your specific needs. Contact us for a free consultation.' 
                : 'خبراء التأمين لدينا جاهزون لمساعدتك في اختيار التغطية المثالية لاحتياجاتك الخاصة. اتصل بنا للحصول على استشارة مجانية.'}
            </p>
            <div className="cta-buttons">
              <motion.button
                className="cta-button primary"
                whileHover={{ scale: 1.05, boxShadow: '0 10px 25px rgba(109, 40, 217, 0.3)' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/contact')}
              >
                {currentLanguage === 'en' ? 'Contact Us Now' : 'اتصل بنا الآن'}
              </motion.button>
              <motion.button
                className="cta-button secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.location.href = 'tel:+218211234567'}
              >
                <span className="phone-icon">📞</span>
                {currentLanguage === 'en' ? 'Call: +218 21 123 4567' : 'اتصل: 218 21 123 4567'}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;

// import React, { useState } from 'react';
// import { motion } from 'framer-motion';
// import { useSelector } from 'react-redux';
// import { useNavigate } from 'react-router-dom';
// import Header from '../components/Header/Header';
// import './ServicesPage.css';

// const ServicesPage = () => {
//   const navigate = useNavigate();
//   const { currentLanguage } = useSelector((state) => state.language);
//   const [activeCategory, setActiveCategory] = useState('all');
//   const [searchTerm, setSearchTerm] = useState('');

  
//   // بيانات التأمينات الكاملة
//   const insuranceServices = [
//     {
//       id: 1,
//       category: 'vehicle',
//       icon: '🚗',
//       title: {
//         en: 'Compulsory Car Insurance',
//         ar: 'تأمين المركبات الإلزامي'
//       },
//       description: {
//         en: 'Mandatory insurance for all vehicles in Libya as per Libyan law. Covers third-party liability for bodily injury and property damage.',
//         ar: 'تأمين إلزامي لجميع المركبات في ليبيا وفقًا للقانون الليبي. يغطي مسؤولية الطرف الثالث للإصابة الجسدية والأضرار المادية.'
//       },
//       features: {
//         en: [
//           'Covers third-party bodily injury up to 500,000 LYD',
//           'Property damage coverage up to 100,000 LYD',
//           'Valid throughout Libya',
//           'Issued immediately'
//         ],
//         ar: [
//           'يغطي إصابة الطرف الثالث حتى 500,000 دينار ليبي',
//           'تغطية الأضرار المادية حتى 100,000 دينار ليبي',
//           'ساري المفعول في جميع أنحاء ليبيا',
//           'يصدر فورًا'
//         ]
//       },
//       legalReference: {
//         en: 'Libyan Insurance Law No. 20 of 2010',
//         ar: 'قانون التأمين الليبي رقم 20 لسنة 2010'
//       }
//     },
//     {
//       id: 2,
//       category: 'vehicle',
//       icon: '🚙',
//       title: {
//         en: 'Complementary Car Insurance',
//         ar: 'تأمين المركبات التكميلي'
//       },
//       description: {
//         en: 'Comprehensive coverage beyond compulsory insurance. Includes theft, fire, natural disasters, and damage to your own vehicle.',
//         ar: 'تغطية شاملة تتجاوز التأمين الإلزامي. تشمل السرقة، الحريق، الكوارث الطبيعية، والأضرار لمركبتك الخاصة.'
//       },
//       features: {
//         en: [
//           'Coverage for theft and burglary',
//           'Fire and natural disaster protection',
//           'Accidental damage to your vehicle',
//           'Optional glass breakage coverage'
//         ],
//         ar: [
//           'تغطية السرقة والسطو',
//           'حماية من الحريق والكوارث الطبيعية',
//           'أضرار عرضية لمركبتك',
//           'تغطية كسر الزجاج اختيارية'
//         ]
//       }
//     },
//     {
//       id: 3,
//       category: 'travel',
//       icon: '✈️',
//       title: {
//         en: 'Travelers Insurance',
//         ar: 'تأمين المسافرين'
//       },
//       description: {
//         en: 'Comprehensive protection for travelers worldwide. Covers medical emergencies, trip cancellation, lost luggage, and personal accidents.',
//         ar: 'حماية شاملة للمسافرين حول العالم. تغطي الطوارئ الطبية، إلغاء الرحلة، فقدان الأمتعة، والحوادث الشخصية.'
//       },
//       features: {
//         en: [
//           'Emergency medical expenses up to $100,000',
//           'Trip cancellation/interruption',
//           'Lost/delayed baggage',
//           '24/7 emergency assistance'
//         ],
//         ar: [
//           'المصاريف الطبية الطارئة حتى 100,000 دولار',
//           'إلغاء/مقاطعة الرحلة',
//           'الأمتعة المفقودة/المتأخرة',
//           'مساعدة طوارئ على مدار الساعة'
//         ]
//       }
//     },
//     {
//       id: 4,
//       category: 'aviation',
//       icon: '🛩️',
//       title: {
//         en: 'Aviation Insurance',
//         ar: 'تأمين الطيران'
//       },
//       description: {
//         en: 'Specialized insurance for aircraft owners, operators, and aviation-related businesses. Covers hull damage, liability, and passenger risks.',
//         ar: 'تأمين متخصص لملاك الطائرات والمشغلين والشركات المتعلقة بالطيران. يغطي أضرار الهيكل، المسؤولية، ومخاطر الركاب.'
//       },
//       features: {
//         en: [
//           'Hull all-risk coverage',
//           'Third-party liability',
//           'Passenger legal liability',
//           'War and terrorism risk coverage'
//         ],
//         ar: [
//           'تغطية جميع مخاطر الهيكل',
//           'مسؤولية الطرف الثالث',
//           'المسؤولية القانونية للركاب',
//           'تغطية مخاطر الحرب والإرهاب'
//         ]
//       }
//     },
//     {
//       id: 5,
//       category: 'property',
//       icon: '🔥',
//       title: {
//         en: 'Fire Insurance',
//         ar: 'تأمين الحريق'
//       },
//       description: {
//         en: 'Protection against fire damage to residential, commercial, and industrial properties. Includes coverage for fire, explosion, lightning, and smoke damage.',
//         ar: 'حماية ضد أضرار الحريق للممتلكات السكنية والتجارية والصناعية. تشمل التغطية للحريق، الانفجار، الصواعق، وأضرار الدخان.'
//       },
//       features: {
//         en: [
//           'Fire and explosion coverage',
//           'Lightning and thunderstorm damage',
//           'Smoke and soot damage',
//           'Alternative accommodation costs'
//         ],
//         ar: [
//           'تغطية الحريق والانفجار',
//           'أضرار الصواعق والعواصف الرعدية',
//           'أضرار الدخان والسخام',
//           'تكاليف الإقامة البديلة'
//         ]
//       }
//     },
//     {
//       id: 6,
//       category: 'energy',
//       icon: '⚡',
//       title: {
//         en: 'Energy Insurance',
//         ar: 'تأمين الطاقة'
//       },
//       description: {
//         en: 'Specialized coverage for energy sector projects including oil, gas, and renewable energy. Designed for construction, operation, and liability risks.',
//         ar: 'تغطية متخصصة لمشاريع قطاع الطاقة بما في ذلك النفط والغاز والطاقة المتجددة. مصممة لمخاطر البناء والتشغيل والمسؤولية.'
//       },
//       features: {
//         en: [
//           'Construction all-risk insurance',
//           'Operational risk coverage',
//           'Business interruption',
//           'Environmental liability'
//         ],
//         ar: [
//           'تأمين جميع مخاطر البناء',
//           'تغطية مخاطر التشغيل',
//           'انقطاع الأعمال',
//           'المسؤولية البيئية'
//         ]
//       }
//     },
//     {
//       id: 7,
//       category: 'engineering',
//       icon: '🏗️',
//       title: {
//         en: 'Engineering Insurance',
//         ar: 'تأمين الهندسة'
//       },
//       description: {
//         en: 'Comprehensive coverage for construction and engineering projects. Protects against material damage, third-party liability, and construction risks.',
//         ar: 'تغطية شاملة لمشاريع البناء والهندسة. يحمي من الأضرار المادية، مسؤولية الطرف الثالث، ومخاطر البناء.'
//       },
//       features: {
//         en: [
//           'Contractors all-risk insurance',
//           'Erection all-risk insurance',
//           'Machinery breakdown',
//           'Third-party liability'
//         ],
//         ar: [
//           'تأمين جميع مخاطر المقاولين',
//           'تأمين جميع مخاطر التركيب',
//           'تلف الآلات',
//           'مسؤولية الطرف الثالث'
//         ]
//       }
//     },
//     {
//       id: 8,
//       category: 'personal',
//       icon: '🚑',
//       title: {
//         en: 'Personal Accident Insurance',
//         ar: 'تأمين الحوادث الشخصية'
//       },
//       description: {
//         en: 'Financial protection against accidental injuries, disability, or death. Provides lump sum payments for medical expenses and income replacement.',
//         ar: 'حماية مالية ضد الإصابات العرضية، الإعاقة، أو الوفاة. يوفر دفعات مبلغ مقطوع للمصاريف الطبية واستبدال الدخل.'
//       },
//       features: {
//         en: [
//           'Accidental death coverage',
//           'Permanent total/partial disability',
//           'Temporary total disability',
//           'Medical expenses reimbursement'
//         ],
//         ar: [
//           'تغطية الوفاة العرضية',
//           'إعاقة كلية/جزئية دائمة',
//           'إعاقة كلية مؤقتة',
//           'استرداد المصاريف الطبية'
//         ]
//       }
//     },
//     {
//       id: 9,
//       category: 'health',
//       icon: '🏥',
//       title: {
//         en: 'Medical Insurance',
//         ar: 'التأمين الطبي'
//       },
//       description: {
//         en: 'Comprehensive healthcare coverage for individuals, families, and corporate groups. Includes hospitalization, outpatient care, and medication.',
//         ar: 'تغطية رعاية صحية شاملة للأفراد والعائلات والمجموعات الشركاتية. تشمل الاستشفاء، الرعاية الخارجية، والأدوية.'
//       },
//       features: {
//         en: [
//           'Inpatient hospitalization',
//           'Outpatient clinic visits',
//           'Emergency room services',
//           'Prescription medication'
//         ],
//         ar: [
//           'الاستشفاء الداخلي',
//           'زيارات العيادات الخارجية',
//           'خدمات غرفة الطوارئ',
//           'الأدوية الموصوفة'
//         ]
//       }
//     },
//     {
//       id: 10,
//       category: 'takaful',
//       icon: '🕌',
//       title: {
//         en: 'Social Takaful Insurance',
//         ar: 'التأمين التعاوني (التكافل)'
//       },
//       description: {
//         en: 'Sharia-compliant cooperative insurance based on mutual assistance and shared responsibility. All operations comply with Islamic principles.',
//         ar: 'تأمين تعاوني متوافق مع الشريعة الإسلامية قائم على التعاون والمسؤولية المشتركة. جميع العمليات تتوافق مع المبادئ الإسلامية.'
//       },
//       features: {
//         en: [
//           'Sharia-compliant operations',
//           'No interest (riba) involved',
//           'Risk-sharing principle',
//           'Charity (tabarru) contributions'
//         ],
//         ar: [
//           'عمليات متوافقة مع الشريعة',
//           'لا تتعامل بالربا',
//           'مبدأ تقاسم المخاطر',
//           'مساهمات خيرية (تبرع)'
//         ]
//       }
//     },
//     {
//       id: 11,
//       category: 'property',
//       icon: '🏘️',
//       title: {
//         en: 'Miscellaneous Accidents (Properties)',
//         ar: 'تأمين الحوادث المتنوعة (الممتلكات)'
//       },
//       description: {
//         en: 'Coverage for various property-related accidents including water damage, impact damage, theft, and malicious acts.',
//         ar: 'تغطية لمختلف الحوادث المتعلقة بالممتلكات بما في ذلك أضرار المياه، أضرار الاصطدام، السرقة، والأعمال الخبيثة.'
//       },
//       features: {
//         en: [
//           'Water damage from plumbing',
//           'Impact by vehicles/animals',
//           'Theft and burglary',
//           'Malicious damage'
//         ],
//         ar: [
//           'أضرار المياه من السباكة',
//           'اصطدام بالمركبات/الحيوانات',
//           'السرقة والسطو',
//           'أضرار خبيثة'
//         ]
//       }
//     },
//     {
//       id: 12,
//       category: 'liability',
//       icon: '⚖️',
//       title: {
//         en: 'Miscellaneous Accidents (Liabilities)',
//         ar: 'تأمين الحوادث المتنوعة (المسؤوليات)'
//       },
//       description: {
//         en: 'Protection against third-party liability claims for bodily injury or property damage arising from various accidents.',
//         ar: 'حماية ضد مطالبات المسؤولية تجاه الغير للإصابة الجسدية أو الأضرار المادية الناتجة عن حوادث متنوعة.'
//       },
//       features: {
//         en: [
//           'Public liability coverage',
//           'Product liability',
//           'Professional indemnity',
//           'Employers liability'
//         ],
//         ar: [
//           'تغطية المسؤولية العامة',
//           'مسؤولية المنتج',
//           'التعويض المهني',
//           'مسؤولية صاحب العمل'
//         ]
//       }
//     },
//     {
//       id: 13,
//       category: 'property',
//       icon: '📦',
//       title: {
//         en: 'Objects Insurance',
//         ar: 'تأمين الأشياء'
//       },
//       description: {
//         en: 'Coverage for valuable objects, equipment, and assets against theft, damage, loss, or destruction.',
//         ar: 'تغطية للأشياء القيمة والمعدات والأصول ضد السرقة، التلف، الفقد، أو التدمير.'
//       },
//       features: {
//         en: [
//           'All-risk coverage for valuables',
//           'Equipment breakdown',
//           'Transit insurance',
//           'Stock and inventory coverage'
//         ],
//         ar: [
//           'تغطية جميع المخاطر للأشياء القيمة',
//           'تلف المعدات',
//           'تأمين النقل',
//           'تغطية المخزون والجرد'
//         ]
//       }
//     }
//   ];

//   // فئات التأمين
//   const categories = [
//     { id: 'all', label: { en: 'All Services', ar: 'جميع الخدمات' } },
//     { id: 'vehicle', label: { en: 'Vehicle Insurance', ar: 'تأمين المركبات' } },
//     { id: 'property', label: { en: 'Property Insurance', ar: 'تأمين الممتلكات' } },
//     { id: 'travel', label: { en: 'Travel Insurance', ar: 'تأمين السفر' } },
//     { id: 'health', label: { en: 'Health Insurance', ar: 'التأمين الصحي' } },
//     { id: 'liability', label: { en: 'Liability Insurance', ar: 'تأمين المسؤولية' } },
//     { id: 'engineering', label: { en: 'Engineering Insurance', ar: 'تأمين الهندسة' } },
//     { id: 'takaful', label: { en: 'Takaful Insurance', ar: 'التأمين التكافلي' } }
//   ];

//   // تصفية الخدمات بناءً على الفئة والبحث
//   const filteredServices = insuranceServices.filter(service => {
//     const matchesCategory = activeCategory === 'all' || service.category === activeCategory;
//     const matchesSearch = searchTerm === '' || 
//       service.title[currentLanguage].toLowerCase().includes(searchTerm.toLowerCase()) ||
//       service.description[currentLanguage].toLowerCase().includes(searchTerm.toLowerCase());
//     return matchesCategory && matchesSearch;
//   });

//   return (
//     <div className="services-page">
//       <Header />
      
//       {/* Hero Section */}
//       <section className="services-hero">
//         <div className="container">
//           <motion.div
//             initial={{ opacity: 0, y: 50 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.8 }}
//             className="hero-content"
//           >
//             <h1>
//               {currentLanguage === 'en' 
//                 ? 'Our Insurance Services' 
//                 : 'خدماتنا التأمينية'}
//             </h1>
//             <p>
//               {currentLanguage === 'en' 
//                 ? 'Comprehensive protection solutions tailored to your needs. Discover our range of insurance services designed to provide peace of mind and financial security.' 
//                 : 'حلول حماية شاملة مصممة خصيصًا لاحتياجاتك. اكتشف مجموعة خدماتنا التأمينية المصممة لتوفير راحة البال والأمن المالي.'}
//             </p>
//           </motion.div>
//         </div>
//       </section>

//       {/* الفلاتر والبحث */}
//       <section className="services-filters">
//         <div className="container">
//           <div className="filters-container">
//             {/* شريط البحث */}
//             <div className="search-container">
//               <input
//                 type="text"
//                 placeholder={currentLanguage === 'en' ? 'Search services...' : 'ابحث في الخدمات...'}
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 className="search-input"
//               />
//               <span className="search-icon">🔍</span>
//             </div>

//             {/* فئات التأمين */}
//             <div className="categories-container">
//               <div className="categories-scroll">
//                 {categories.map((category) => (
//                   <motion.button
//                     key={category.id}
//                     className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
//                     onClick={() => setActiveCategory(category.id)}
//                     whileHover={{ scale: 1.05 }}
//                     whileTap={{ scale: 0.95 }}
//                   >
//                     {category.label[currentLanguage]}
//                   </motion.button>
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* قائمة الخدمات */}
//       <section className="services-list">
//         <div className="container">
//           <div className="services-grid">
//             {filteredServices.map((service, index) => (
//               <motion.div
//                 key={service.id}
//                 className="service-card"
//                 initial={{ opacity: 0, y: 50 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ duration: 0.5, delay: index * 0.1 }}
//                 whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)' }}
//               >
//                 <div className="service-icon">
//                   <span>{service.icon}</span>
//                 </div>
                
//                 <div className="service-content">
//                   <h3>{service.title[currentLanguage]}</h3>
//                   <p className="service-description">{service.description[currentLanguage]}</p>
                  
//                   <div className="service-features">
//                     <h4>{currentLanguage === 'en' ? 'Key Features:' : 'المميزات الرئيسية:'}</h4>
//                     <ul>
//                       {service.features[currentLanguage].map((feature, idx) => (
//                         <li key={idx}>{feature}</li>
//                       ))}
//                     </ul>
//                   </div>

//                   {service.legalReference && (
//                     <div className="legal-reference">
//                       <strong>{currentLanguage === 'en' ? 'Legal Reference:' : 'المرجع القانوني:'}</strong>
//                       <p>{service.legalReference[currentLanguage]}</p>
//                     </div>
//                   )}
//                 </div>

//                 <div className="service-actions">
//                   <motion.button
//                     className="inquire-btn"
//                     whileHover={{ scale: 1.05 }}
//                     whileTap={{ scale: 0.95 }}
//                     onClick={() => navigate('/contact')}
//                   >
//                     {currentLanguage === 'en' ? 'Request Quote' : 'طلب عرض سعر'}
//                   </motion.button>
//                   <motion.button
//                     className="details-btn"
//                     whileHover={{ scale: 1.05 }}
//                     whileTap={{ scale: 0.95 }}
//                   >
//                     {currentLanguage === 'en' ? 'More Details' : 'المزيد من التفاصيل'}
//                   </motion.button>
//                 </div>
//               </motion.div>
//             ))}
//           </div>
//         </div>
//       </section>

//       {/* قسم الأسئلة الشائعة */}
//       <section className="faq-section">
//         <div className="container">
//           <motion.div
//             initial={{ opacity: 0 }}
//             whileInView={{ opacity: 1 }}
//             transition={{ duration: 0.8 }}
//             viewport={{ once: true }}
//           >
//             <h2>{currentLanguage === 'en' ? 'Frequently Asked Questions' : 'الأسئلة الشائعة'}</h2>
            
//             <div className="faq-grid">
//               <div className="faq-item">
//                 <h3>{currentLanguage === 'en' ? 'How to choose the right insurance?' : 'كيف أختار التأمين المناسب؟'}</h3>
//                 <p>
//                   {currentLanguage === 'en' 
//                     ? 'Consider your assets, risks, and budget. Our experts can help you assess your needs and recommend suitable coverage.' 
//                     : 'فكر في أصولك ومخاطرك وميزانيتك. يمكن لخبرائنا مساعدتك في تقييم احتياجاتك والتوصية بالتغطية المناسبة.'}
//                 </p>
//               </div>
              
//               <div className="faq-item">
//                 <h3>{currentLanguage === 'en' ? 'What documents are required?' : 'ما هي المستندات المطلوبة؟'}</h3>
//                 <p>
//                   {currentLanguage === 'en' 
//                     ? 'Typically: ID copy, proof of ownership, and previous insurance details (if applicable). Requirements vary by insurance type.' 
//                     : 'عادة: نسخة من الهوية، إثبات الملكية، وتفاصيل التأمين السابق (إن وجد). المتطلبات تختلف حسب نوع التأمين.'}
//                 </p>
//               </div>
              
//               <div className="faq-item">
//                 <h3>{currentLanguage === 'en' ? 'How long does processing take?' : 'كم تستغرق المعالجة؟'}</h3>
//                 <p>
//                   {currentLanguage === 'en' 
//                     ? 'Compulsory car insurance: immediate. Other insurances: 1-3 business days depending on complexity and documents.' 
//                     : 'تأمين المركبات الإلزامي: فوري. أنواع التأمين الأخرى: 1-3 أيام عمل حسب التعقيد والمستندات.'}
//                 </p>
//               </div>
              
//               <div className="faq-item">
//                 <h3>{currentLanguage === 'en' ? 'Can I cancel my policy?' : 'هل يمكنني إلغاء وثيقتي؟'}</h3>
//                 <p>
//                   {currentLanguage === 'en' 
//                     ? 'Yes, most policies can be cancelled. Refunds are calculated based on elapsed time and policy terms.' 
//                     : 'نعم، يمكن إلغاء معظم الوثائق. يتم حساب المبالغ المستردة بناءً على الوقت المنقضى وشروط الوثيقة.'}
//                 </p>
//               </div>
//             </div>
//           </motion.div>
//         </div>
//       </section>

//       {/* قسم الـ CTA */}
//       <section className="services-cta">
//         <div className="container">
//           <motion.div
//             initial={{ opacity: 0, scale: 0.9 }}
//             whileInView={{ opacity: 1, scale: 1 }}
//             transition={{ duration: 0.8 }}
//             viewport={{ once: true }}
//             className="cta-content"
//           >
//             <h2>{currentLanguage === 'en' ? 'Need Personalized Advice?' : 'تحتاج إلى نصيحة شخصية؟'}</h2>
//             <p>
//               {currentLanguage === 'en' 
//                 ? 'Our insurance experts are ready to help you choose the perfect coverage for your specific needs.' 
//                 : 'خبراء التأمين لدينا جاهزون لمساعدتك في اختيار التغطية المثالية لاحتياجاتك الخاصة.'}
//             </p>
//             <motion.button
//               className="cta-button"
//               whileHover={{ scale: 1.05, boxShadow: '0 10px 25px rgba(109, 40, 217, 0.3)' }}
//               whileTap={{ scale: 0.95 }}
//               onClick={() => navigate('/contact')}
//             >
//               {currentLanguage === 'en' ? 'Contact Us Now' : 'اتصل بنا الآن'}
//             </motion.button>
//           </motion.div>
//         </div>
//       </section>
//     </div>
//   );
// };

// export default ServicesPage;